/**
 * Google Play Billing inside the Android app (TWA), through Chrome's Digital
 * Goods API + Payment Request. The server verifies every purchase with Google
 * before granting credits (apps/api/src/billing); this file never grants.
 *
 * In a normal browser the Digital Goods API is absent: purchases are not
 * offered there (the paywall points to the Android app instead).
 */
import type { CreditPricingConfig } from '@loomia/shared';
import { api } from '../api/client.js';

const PLAY_METHOD = 'https://play.google.com/billing';
export const PLAY_STORE_URL = 'https://play.google.com/store/apps/details?id=com.loomia.app';

interface ItemDetails {
  itemId: string;
  price: { currency: string; value: string };
}
interface PurchaseDetails {
  itemId: string;
  purchaseToken: string;
}
interface DigitalGoodsService {
  getDetails(itemIds: string[]): Promise<ItemDetails[]>;
  listPurchases(): Promise<PurchaseDetails[]>;
}
type DgWindow = Window & { getDigitalGoodsService?: (method: string) => Promise<DigitalGoodsService> };

let servicePromise: Promise<DigitalGoodsService | null> | null = null;

/** The Play billing service, or null outside the Android app. */
export function playBilling(): Promise<DigitalGoodsService | null> {
  servicePromise ??= (async () => {
    const w = window as DgWindow;
    if (typeof w.getDigitalGoodsService !== 'function') return null;
    try {
      return await w.getDigitalGoodsService(PLAY_METHOD);
    } catch {
      return null; // not installed from Play, or billing unavailable
    }
  })();
  return servicePromise;
}

/**
 * Inside the Android app, show Play's own prices (the user's currency and
 * tax) instead of the USD list prices, and drop the web payment-method tiles.
 */
export async function withStorePrices(p: CreditPricingConfig): Promise<CreditPricingConfig> {
  const svc = await playBilling();
  if (!svc) return p;
  try {
    const ids = [...p.packs.map((x) => x.id), ...p.subscriptions.map((x) => x.id)];
    const details = new Map((await svc.getDetails(ids)).map((d) => [d.itemId, d.price]));
    const local = <T extends { id: string; priceMinorUnits: number; currency: string }>(x: T): T => {
      const price = details.get(x.id);
      return price ? { ...x, priceMinorUnits: Math.round(Number(price.value) * 100), currency: price.currency } : x;
    };
    return { ...p, packs: p.packs.map(local), subscriptions: p.subscriptions.map(local), paymentMethods: [] };
  } catch {
    return { ...p, paymentMethods: [] };
  }
}

export interface BuyResult {
  status: 'granted' | 'pending' | 'cancelled';
  credits: number;
}

/** Opens Play's purchase sheet for `productId`, then has the server verify it. */
export async function buyWithPlay(productId: string): Promise<BuyResult> {
  const request = new PaymentRequest([{ supportedMethods: PLAY_METHOD, data: { sku: productId } }], {
    total: { label: 'Total', amount: { currency: 'USD', value: '0' } },
  });
  let response: PaymentResponse;
  try {
    response = await request.show();
  } catch {
    return { status: 'cancelled', credits: 0 }; // closed the sheet
  }
  const token = (response.details as { purchaseToken?: string }).purchaseToken;
  if (!token) {
    await response.complete('fail');
    throw new Error('Google Play returned no purchase token.');
  }
  try {
    const res = await api.playPurchase(productId, token);
    await response.complete('success');
    return { status: res.status, credits: res.credits };
  } catch (err) {
    await response.complete('fail');
    throw err;
  }
}

/** Re-sends every purchase Play still knows about; returns credits newly granted. */
export async function restorePlayPurchases(): Promise<number | null> {
  const svc = await playBilling();
  if (!svc) return null;
  let added = 0;
  for (const p of await svc.listPurchases()) {
    const before = (await api.getBalance()).balance;
    const res = await api.playPurchase(p.itemId, p.purchaseToken).catch(() => null);
    if (res?.status === 'granted') added += Math.max(0, res.balance - before);
  }
  return added;
}
